import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../../components/Header";
import LoadingContainer from "../../components/LoadingContainer";
import { getContactEmailTimeline } from "../../api/axios";
import useAuth from "../../hooks/useAuth";
import { day } from "../../utilities/Utils";

const getTime = (dateRaw) => {
  if (!dateRaw) {
    return "";
  }

  const dateTime = new Date(dateRaw);

  if (Number.isNaN(dateTime.getTime())) {
    return "";
  }

  const hours = dateTime.getHours().toString().padStart(2, "0");
  const minutes = dateTime.getMinutes().toString().padStart(2, "0");

  return `${hours}:${minutes}`;
};

const getSubject = (item) => {
  if (item.type === "reenvio" && item.resend_subject) {
    return item.resend_subject;
  }

  return item.subject || item.email_name || `Envío #${item.email_id}`;
};

const getBrandTextClass = (brandName) => {
  const normalizedBrandName = String(brandName ?? "").trim().toLowerCase();

  if (normalizedBrandName === "royal canin") {
    return "email-timeline__brand--royal-canin";
  }

  if (normalizedBrandName === "eukanuba") {
    return "email-timeline__brand--eukanuba";
  }

  if (normalizedBrandName === "mi vet shop") {
    return "email-timeline__brand--mi-vet-shop";
  }

  return "";
};

const getLatestUnsubscribedEvent = (events) => {
  return events
    .filter((item) => Boolean(item.date_unsubscribed))
    .sort((a, b) => new Date(b.date_unsubscribed) - new Date(a.date_unsubscribed))[0] ?? null;
};

const ContactEmailTimeline = () => {
  const { idContact } = useParams();
  const { auth } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contact, setContact] = useState(null);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        const data = await getContactEmailTimeline(idContact, auth);
        setContact(data.contact ?? null);
        setTimeline(Array.isArray(data.results) ? data.results : []);
        setError(null);
      } catch (fetchError) {
        setContact(null);
        setTimeline([]);
        setError(fetchError);
      }

      setIsLoading(false);
    };

    fetchData();
  }, [auth, idContact]);

  const hasTimeline = timeline.length > 0;
  const latestUnsubscribedEvent = getLatestUnsubscribedEvent(timeline);
  const hasComments = Boolean(String(contact?.comments ?? "").trim());

  return (
    <>
      <Header />

      <div className="content">
        <h2 className="page_title">Histórico de emails</h2>

        <div className="timeline-page">
          <div className="timeline-page__header">
            <div>
              <Link className="vinculo timeline-page__back" to="/contacts">
                Volver a destinatarios
              </Link>
              <h3>{contact?.email || `Destinatario #${idContact}`}</h3>
              <div className="timeline-page__contact-meta">
                <span>ID #{idContact}</span>
                {contact?.brand_name ? (
                  <span className={`email-timeline__brand ${getBrandTextClass(contact.brand_name)}`.trim()}>
                    {contact.brand_name}
                  </span>
                ) : null}
              </div>
              {contact?.first_name || contact?.last_name ? (
                <p>{`${contact?.first_name ?? ""} ${contact?.last_name ?? ""}`.trim()}</p>
              ) : null}

              <div className="timeline-page__summary">
                <span className={`email-timeline__state ${contact?.active === "0" ? "email-timeline__state--warn" : "email-timeline__state--ok"}`}>
                  {contact?.active === "0" ? "Contacto inactivo" : "Contacto activo"}
                </span>
                {latestUnsubscribedEvent ? (
                  <span className="email-timeline__state email-timeline__state--warn">
                    Baja en envío #{latestUnsubscribedEvent.email_id} · {day(latestUnsubscribedEvent.date_unsubscribed)}
                  </span>
                ) : (
                  <span className="email-timeline__state">Sin bajas registradas en envíos</span>
                )}
              </div>

              {hasComments ? (
                <div className="timeline-page__comments">
                  <strong>Comentarios</strong>
                  <p>{contact.comments}</p>
                </div>
              ) : null}
            </div>
          </div>

          {error ? (
            <div className="errmsg">No se pudo cargar el histórico de emails.</div>
          ) : null}

          <LoadingContainer isLoading={isLoading}>
            {!isLoading && !hasTimeline ? (
              <div className="no_results">No se encontraron emails enviados para este destinatario.</div>
            ) : null}

            {!isLoading && hasTimeline ? (
              <ol className="email-timeline">
                {timeline.map((item) => {
                  const isResend = item.type === "reenvio";
                  const hasOpened = Boolean(item.date_opened);
                  const hasUnsubscribed = Boolean(item.date_unsubscribed);
                  const hasBounced = item.bounced === "1" || item.bounced === 1 || item.postfix_status === "bounced";
                  const hasClicks = Number(item.clicks_count ?? 0) > 0;

                  return (
                    <li className="email-timeline__item" key={item.id}>
                      <div className="email-timeline__marker" />
                      <article className="email-timeline__card">
                        <div className="email-timeline__topline">
                          <div className="email-timeline__identity">
                            {item.email_id ? (
                              <Link className="vinculo email-timeline__id" to={`/emails/${item.email_id}`}>
                                Envío #{item.email_id}
                              </Link>
                            ) : null}
                            {item.brand_name ? (
                              <span className={`email-timeline__brand ${getBrandTextClass(item.brand_name)}`.trim()}>
                                {item.brand_name}
                              </span>
                            ) : null}
                            <span className={`email-timeline__badge ${isResend ? "email-timeline__badge--resend" : ""}`}>
                              {isResend ? "Reenvío" : "Original"}
                            </span>
                          </div>
                          <span className="email-timeline__date">
                            {day(item.date_sent)} {getTime(item.date_sent)}
                          </span>
                        </div>

                        <h4>{getSubject(item)}</h4>

                        <div className="email-timeline__meta">
                          {item.campaign_name ? <span>{item.campaign_name}</span> : null}
                        </div>

                        <div className="email-timeline__states">
                          <span className="email-timeline__state email-timeline__state--sent">Enviado</span>
                          <span className={`email-timeline__state ${hasOpened ? "email-timeline__state--ok" : ""}`}>
                            {hasOpened ? `Abierto ${day(item.date_opened)}` : "No abierto"}
                          </span>
                          <span className={`email-timeline__state ${hasClicks ? "email-timeline__state--ok" : ""}`}>
                            {hasClicks ? `${item.clicks_count} click${Number(item.clicks_count) === 1 ? "" : "s"}` : "Sin clicks"}
                          </span>
                          {hasBounced ? <span className="email-timeline__state email-timeline__state--warn">Rebotado</span> : null}
                          {hasUnsubscribed ? (
                            <span className="email-timeline__state email-timeline__state--warn">
                              Baja {day(item.date_unsubscribed)}
                            </span>
                          ) : null}
                        </div>

                        {hasClicks ? (
                          <details className="email-timeline__clicks">
                            <summary>URLs clickeadas</summary>
                            <ul>
                              {item.click_urls.map((url) => (
                                <li key={url}>
                                  <a href={url} target="_blank" rel="noreferrer">
                                    {url}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </details>
                        ) : null}
                      </article>
                    </li>
                  );
                })}
              </ol>
            ) : null}
          </LoadingContainer>
        </div>
      </div>
    </>
  );
};

export default ContactEmailTimeline;
